import React, { useMemo, useState } from "react";
import { X } from "lucide-react";

const importantValueLabel = (key) =>
  ({
    "cmi.core.lesson_status": "Lesson status",
    "cmi.completion_status": "Completion",
    "cmi.success_status": "Success",
    "cmi.core.score.raw": "Score brut",
    "cmi.score.raw": "Score brut",
    "cmi.score.scaled": "Score scaled",
    "cmi.core.lesson_location": "Lesson location",
    "cmi.location": "Location",
    "cmi.core.total_time": "Temps total",
    "cmi.total_time": "Temps total",
    "cmi.suspend_data": "Suspend data",
  }[key] || key);

const formatDate = (value) => (value ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value)) : "-");

const groupByAttemptAndSco = (items) =>
  (items || []).reduce((acc, item) => {
    const key = `${item.attemptId || "unknown-attempt"}:${item.scoId || "unknown-sco"}`;
    if (!acc[key]) {
      acc[key] = {
        attemptId: item.attemptId,
        scoId: item.scoId,
        scoTitle: item.scoTitle || "SCO",
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {});

const resultClass = (result) => {
  const normalized = String(result || "").toLowerCase();
  if (["correct", "passed", "true"].includes(normalized)) return "is-correct";
  if (["wrong", "incorrect", "failed", "false"].includes(normalized)) return "is-wrong";
  return "is-neutral";
};

const resultBucket = (result) => {
  const normalized = String(result || "").toLowerCase();
  if (["correct", "passed", "true"].includes(normalized)) return "correct";
  if (["wrong", "incorrect", "failed", "false"].includes(normalized)) return "incorrect";
  return "neutral";
};

const resultLabel = (result) => {
  const bucket = resultBucket(result);
  if (bucket === "correct") return "Correct";
  if (bucket === "incorrect") return "Incorrect";
  return result || "Neutre";
};

const scoreValue = (item) => {
  if (item.scoreScaled != null) return Math.round(item.scoreScaled * 1000) / 10;
  return item.scoreRaw ?? "-";
};

const buildQuizSummary = (detail) => {
  const interactions = detail?.interactions || [];
  const correct = interactions.filter((item) => resultBucket(item.result) === "correct").length;
  const incorrect = interactions.filter((item) => resultBucket(item.result) === "incorrect").length;
  const neutral = interactions.length - correct - incorrect;
  const attempts = detail?.attempts || [];
  const scored = interactions.map((item) => item.weighting).filter((value) => typeof value === "number");
  const attemptScores = attempts
    .map((attempt) => (attempt.scoreScaled != null ? Number(attempt.scoreScaled) * 100 : attempt.scoreRaw))
    .filter((value) => typeof value === "number" && !Number.isNaN(value));
  const scoreSource = scored.length ? scored : attemptScores;
  const averageScore = scoreSource.length ? Math.round((scoreSource.reduce((sum, value) => sum + value, 0) / scoreSource.length) * 10) / 10 : null;
  const hasPassed = attempts.some((attempt) => String(attempt.successStatus || attempt.lessonStatus || "").toLowerCase() === "passed");
  const hasFailed = attempts.some((attempt) => String(attempt.successStatus || attempt.lessonStatus || "").toLowerCase() === "failed");
  const hasCompleted = attempts.some((attempt) => String(attempt.completionStatus || attempt.lessonStatus || "").toLowerCase() === "completed");
  let status = "En cours";
  if (hasPassed || (interactions.length > 0 && correct > incorrect)) status = "Reussi";
  else if (hasFailed || (interactions.length > 0 && incorrect > correct)) status = "Echoue";
  else if (hasCompleted) status = "Complete";
  return { total: interactions.length, correct, incorrect, neutral, averageScore, status };
};

export default function ScormUserScormDetailModal({ detail, loading, onClose }) {
  const [activeTab, setActiveTab] = useState("attempts");
  const interactionGroups = useMemo(() => groupByAttemptAndSco(detail?.interactions), [detail]);
  const objectiveGroups = useMemo(() => groupByAttemptAndSco(detail?.objectives), [detail]);
  const quizSummary = useMemo(() => buildQuizSummary(detail), [detail]);

  if (!detail && !loading) return null;

  return (
    <div className="scorm-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="scorm-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="scorm-modal-header">
          <div>
            <h2>{loading ? "Chargement..." : detail.fullName}</h2>
            <p>{detail?.email}</p>
          </div>
          <button className="scorm-icon-button" type="button" onClick={onClose} title="Fermer">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="scorm-report-empty">Chargement du detail SCORM...</div>
        ) : (
          <>
            <h3>Contenus SCORM</h3>
            <div className="scorm-modal-grid">
              {detail.contents?.map((content) => (
                <div className="scorm-detail-card" key={content.contentId}>
                  <strong>{content.contentTitle}</strong>
                  <span>{content.status}</span>
                  <span>{content.completionPercent || 0}%</span>
                  <span>{content.currentNodeTitle || content.currentScoTitle || "Aucun SCO courant"}</span>
                </div>
              ))}
            </div>

            <div className="scorm-detail-tabs">
              <button type="button" className={activeTab === "attempts" ? "active" : ""} onClick={() => setActiveTab("attempts")}>
                Tentatives
              </button>
              <button type="button" className={activeTab === "interactions" ? "active" : ""} onClick={() => setActiveTab("interactions")}>
                Interactions
                <span>{detail.interactions?.length || 0}</span>
              </button>
              <button type="button" className={activeTab === "objectives" ? "active" : ""} onClick={() => setActiveTab("objectives")}>
                Objectives
                <span>{detail.objectives?.length || 0}</span>
              </button>
            </div>

            <section className="scorm-quiz-summary">
              <div>
                <span>Quiz interne</span>
                <strong>{quizSummary.status}</strong>
              </div>
              <div>
                <span>Total</span>
                <strong>{quizSummary.total}</strong>
              </div>
              <div>
                <span>Correct</span>
                <strong className="is-correct-text">{quizSummary.correct}</strong>
              </div>
              <div>
                <span>Incorrect</span>
                <strong className="is-wrong-text">{quizSummary.incorrect}</strong>
              </div>
              <div>
                <span>Neutre</span>
                <strong>{quizSummary.neutral}</strong>
              </div>
              <div>
                <span>Score moyen</span>
                <strong>{quizSummary.averageScore ?? "-"}</strong>
              </div>
            </section>

            {activeTab === "attempts" && (
              <div className="scorm-attempt-list">
                {detail.attempts?.length ? (
                  detail.attempts.map((attempt) => (
                    <article className="scorm-attempt" key={attempt.attemptId}>
                      <div className="scorm-attempt-title">
                        <strong>{attempt.scoTitle}</strong>
                        <span>{attempt.status}</span>
                      </div>
                      <div className="scorm-attempt-meta">
                        <span>Tentative {attempt.attemptNumber}</span>
                        <span>Score {attempt.scoreScaled != null ? Math.round(attempt.scoreScaled * 1000) / 10 : attempt.scoreRaw ?? "-"}</span>
                        <span>Dernier commit {formatDate(attempt.lastCommitAt)}</span>
                        <span>Suspend data {attempt.suspendDataPresent ? "present" : "absent"}</span>
                      </div>
                      <div className="scorm-runtime-values">
                        {Object.entries(attempt.runtimeValues || {}).map(([key, value]) => (
                          <div key={key}>
                            <span>{importantValueLabel(key)}</span>
                            <strong>{value}</strong>
                          </div>
                        ))}
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="scorm-report-empty">Aucune tentative enregistree.</div>
                )}
              </div>
            )}

            {activeTab === "interactions" && (
              <div className="scorm-quiz-report">
                {Object.values(interactionGroups).length ? (
                  Object.entries(interactionGroups).map(([groupKey, group]) => (
                    <section className="scorm-quiz-group" key={groupKey}>
                      <h3>Tentative {group.attemptId || "-"} - {group.scoTitle}</h3>
                      <div className="scorm-quiz-table-wrap">
                        <table className="scorm-quiz-table">
                          <thead>
                            <tr>
                              <th>Question</th>
                              <th>Type</th>
                              <th>Reponse</th>
                              <th>Bonne reponse</th>
                              <th>Resultat</th>
                              <th>Score</th>
                              <th>Temps</th>
                              <th>Horodatage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map((interaction) => (
                              <tr key={interaction.interactionDbId || `${interaction.attemptId}-${interaction.interactionIndex}`}>
                                <td>
                                  <strong>{interaction.interactionId || `Interaction ${interaction.interactionIndex + 1}`}</strong>
                                  <span>{interaction.description}</span>
                                </td>
                                <td>{interaction.type || "-"}</td>
                                <td>{interaction.learnerResponse || "-"}</td>
                                <td>{interaction.correctResponse || "-"}</td>
                                <td>
                                  <span className={`scorm-result-badge ${resultClass(interaction.result)}`}>{resultLabel(interaction.result)}</span>
                                </td>
                                <td>{interaction.weighting ?? "-"}</td>
                                <td>{interaction.latency || "-"}</td>
                                <td>{formatDate(interaction.timestamp)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ))
                ) : (
                  <div className="scorm-report-empty">Aucune interaction quiz remontee par le package.</div>
                )}
              </div>
            )}

            {activeTab === "objectives" && (
              <div className="scorm-quiz-report">
                {Object.values(objectiveGroups).length ? (
                  Object.entries(objectiveGroups).map(([groupKey, group]) => (
                    <section className="scorm-quiz-group" key={groupKey}>
                      <h3>Tentative {group.attemptId || "-"} - {group.scoTitle}</h3>
                      <div className="scorm-quiz-table-wrap">
                        <table className="scorm-quiz-table">
                          <thead>
                            <tr>
                              <th>Objectif</th>
                              <th>Completion</th>
                              <th>Success</th>
                              <th>Score</th>
                              <th>Progression</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map((objective) => (
                              <tr key={objective.objectiveDbId || `${objective.attemptId}-${objective.objectiveIndex}`}>
                                <td>{objective.objectiveId || `Objective ${objective.objectiveIndex + 1}`}</td>
                                <td>{objective.completionStatus || "-"}</td>
                                <td>
                                  <span className={`scorm-result-badge ${resultClass(objective.successStatus)}`}>
                                    {objective.successStatus || "unknown"}
                                  </span>
                                </td>
                                <td>{scoreValue(objective)}</td>
                                <td>{objective.progressMeasure ?? "-"}</td>
                                <td>{objective.description || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ))
                ) : (
                  <div className="scorm-report-empty">Aucun objectif remonte par le package.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
